import type {
  GambleOfferDetails,
  RejectedSpinResponse,
  ReplayConfiguration,
  RequestPlayOkResult,
  RoundSnapshot,
  SpinRequest,
  SpinResponse,
  SpinThenPresentThenSettleResult,
} from "@slot/client-engine";
import { ProtocolError, SpinTransportError } from "@slot/client-engine";
import type { SpinFailureKind } from "../system-modal/failureCopy";
import type { BalanceStore } from "../balance/BalanceStore";
import type { BetSystem } from "../bet/BetSystemTypes";
import type { SpinCoordinatorSnapshot, SpinPhase, SpinSpeedMode } from "./SpinCoordinatorTypes";
import type { GambleFlowResult } from "../gamble/GambleTypes";
import type { Log, PresentationBarrier, TimerSleep } from "@slot/presentation";


/** A gamble the server offered on the settled round, until the player takes it or the next spin forfeits it. */
export interface PendingGambleOffer {
  roundIdentifier: string;
  possibleClientActions: string[];
  gambleOfferDetails?: GambleOfferDetails;
}

export interface SpinCoordinatorDeps {
  requestPlay: (req: SpinRequest, cfg: Partial<ReplayConfiguration>) => Promise<SpinThenPresentThenSettleResult>;
  buildSpinRequest: (input?: { requestedFeatureIdentifier?: string }) => SpinRequest;
  buildReplayConfiguration: (mode: SpinSpeedMode) => Partial<ReplayConfiguration>;
  betSystem: BetSystem;
  balanceStore: BalanceStore;
  /** Runs in parallel with the request (the drop-out animation); it releases the "spin" barrier when the grid may reveal. */
  preSpin?: () => Promise<void>;
  onSpeedModeChange?: (mode: SpinSpeedMode) => void;
  onSlamStop?: () => void;
  /** Replays a round the server already holds (a restore). */
  playExistingRound?: (spinResponse: SpinResponse, cfg: Partial<ReplayConfiguration>) => Promise<void>;
  /**
   * Awaited as a round starts, in the requesting phase (decision 71): a spin or a buy before its request goes out, a
   * held round before it replays. The title holds the round until what it needs to show it is in; a rejection is
   * logged and the round goes on, and so it does past `BEFORE_ROUND_MAX_SECONDS` of `timer`.
   */
  beforeRound?: () => Promise<void>;
  /**
   * An engine-time count-down (the UI's runner, never sped up): the bound on `beforeRound`, cancelled as soon as the wait
   * settles. Without it the wait is unbounded.
   */
  timer?: (seconds: number) => TimerSleep;
  /** A contract-valid rejection (insufficient funds …); called before the coordinator idles. */
  onSpinRejected?: (input: { statusCode?: number; statusMessage?: string; balanceAmount?: string }) => Promise<void> | void;
  /**
   * A failed round, with its kind (and the backend's code when its error reply named one, decision 80) so the UI can
   * word it; `presented` when the round had shown its result (the settlement failed), so the grid keeps it. Called
   * after the barrier is released and before the coordinator idles; when wired, the error is consumed.
   */
  onSpinError?: (input: { errorMessage: string; kind: SpinFailureKind; statusMessage?: string; presented: boolean }) => Promise<void> | void;
  log: Log;
  /** The first reveal must not touch the grid before the pre-spin releases the "spin" hold. */
  presentationBarrier?: PresentationBarrier;
  /** Gamble, after the round settles: whether the server offered one, and the flow when the player enters it. */
  shouldOfferGamble?: (roundSnapshot: { possibleClientActions?: string[]; gambleOfferDetails?: GambleOfferDetails }) => boolean;
  onGamblePhase?: (offer: PendingGambleOffer) => Promise<GambleFlowResult | null>;
}

const BARRIER_KEY = "spin";

/**
 * The longest a title's wait holds a round (decision 71): then the round goes on, as when the wait fails. The bet is
 * deducted from the readout before the wait, so a wait that never settled would leave it deducted, the reels rolling
 * and no request sent.
 */
export const BEFORE_ROUND_MAX_SECONDS = 30;

/**
 * The phase machine of a round: idle → requesting → presenting → settling → idle, with the gamble
 * phase the player may enter from idle while an offer stands. Holds the bet lock and the "spin"
 * barrier across a round and releases both on every exit path.
 */
export class SpinCoordinator {
  private phase: SpinPhase = "idle";
  private speedMode: SpinSpeedMode = "normal";
  private lastRoundId?: string;
  /** The current round showed its result: a later failure is a settlement failure. */
  private roundPresented = false;
  private pendingGamble: PendingGambleOffer | null = null;
  private readonly listeners = new Set<(s: SpinCoordinatorSnapshot) => void>();
  /** A fan-out in progress, and a transition that happened during it (a listener started the next round). */
  private emitting = false;
  private emitAgain = false;
  private readonly log: Log;

  public constructor(private readonly deps: SpinCoordinatorDeps) {
    this.log = deps.log;
  }

  public getSnapshot(): SpinCoordinatorSnapshot {
    const betLocked = this.deps.betSystem.isLocked();
    return {
      phase: this.phase,
      speedMode: this.speedMode,
      canSpin: this.phase === "idle" && !betLocked,
      canChangeBet: this.phase === "idle" && !betLocked,
      canSlamStop: this.phase === "requesting" || this.phase === "presenting",
      canGamble: this.pendingGamble !== null && this.phase === "idle" && !betLocked,
      lastRoundId: this.lastRoundId,
    };
  }

  public subscribe(listener: (s: SpinCoordinatorSnapshot) => void): () => void {
    this.listeners.add(listener);
    listener(this.getSnapshot());
    return () => this.listeners.delete(listener);
  }

  public setSpeedMode(mode: SpinSpeedMode): void {
    if (this.speedMode === mode) return;
    this.speedMode = mode;
    this.deps.onSpeedModeChange?.(mode);
    this.emit();
  }

  /**
   * The player's stop. Taken while presenting and, since 2026-09-26, while the request is still in
   * flight (user: remember it): the mechanic holds a stop that comes before the result and lands
   * the reels the moment it arrives. Until then such a press was dropped.
   */
  public slamStop(): void {
    if (!this.getSnapshot().canSlamStop) {
      this.log(`[SpinCoordinator] slamStop ignored phase=${this.phase}`);
      return;
    }
    this.log(`[SpinCoordinator] slamStop phase=${this.phase} round=${this.lastRoundId ?? "?"}`);
    this.deps.onSlamStop?.();
  }

  public async requestSpin(): Promise<void> {
    await this.requestRound({});
  }

  public async requestFeatureSpin(requestedFeatureIdentifier: string): Promise<void> {
    await this.requestRound({ requestedFeatureIdentifier });
  }

  public async enterGamble(): Promise<void> {
    const offer = this.pendingGamble;
    if (this.phase !== "idle" || !offer) {
      this.log(`[SpinCoordinator] enterGamble ignored phase=${this.phase} offer=${offer ? "pending" : "none"}`);
      return;
    }
    if (!this.deps.onGamblePhase) {
      this.log("[SpinCoordinator] enterGamble ignored — no onGamblePhase handler");
      return;
    }
    this.pendingGamble = null;
    this.deps.betSystem.lock("gamble_in_flight");
    this.setPhase("gambling");
    this.log(`[SpinCoordinator] enterGamble start round=${offer.roundIdentifier}`);
    try {
      const result = await this.deps.onGamblePhase(offer);
      this.log(`[SpinCoordinator] enterGamble complete round=${offer.roundIdentifier} outcome=${result?.outcome ?? "skipped"} count=${result?.gambleCount ?? 0}`);
    } catch (e) {
      this.log(`[SpinCoordinator] enterGamble error round=${offer.roundIdentifier} message=${describe(e)}`);
      throw e;
    } finally {
      this.finishIdle();
    }
  }

  /**
   * A round the server already holds (a pending round after a reload): presented like a spin, never
   * requested or settled here. With `keepBetLocked` the round ends idle but leaves the bet locked
   * under boot's reason, so the boot releases it after the settlement (finding 4, 2026-09-14): a
   * spin must not start against a round whose settlement is still in flight.
   */
  public async presentRestoredRound(spinResponse: SpinResponse, options: { keepBetLocked?: boolean } = {}): Promise<void> {
    if (this.phase !== "idle") {
      this.log(`[SpinCoordinator] presentRestoredRound ignored phase=${this.phase}`);
      return;
    }
    const playExistingRound = this.deps.playExistingRound;
    if (!playExistingRound) throw new Error("[SpinCoordinator] presentRestoredRound requires playExistingRound dep");
    this.log(`[SpinCoordinator] presentRestoredRound begin mode=${this.speedMode}`);
    this.deps.betSystem.lock("spin_request_in_flight");
    const preSpin = this.holdBarrierAndPreSpin();
    try {
      // The title's wait, as for a spin (decision 71); without one the round presents at once, as before.
      if (this.deps.beforeRound) {
        this.setPhase("requesting");
        await this.awaitBeforeRound();
      }
      this.setPhase("presenting");
      await Promise.all([playExistingRound(spinResponse, this.deps.buildReplayConfiguration(this.speedMode)), preSpin]);
      this.log("[SpinCoordinator] presenting restored round complete");
      if (options.keepBetLocked) {
        this.deps.betSystem.lock("boot_loading");
        this.setPhase("idle");
      } else {
        this.finishIdle();
      }
    } catch (e) {
      this.log(`[SpinCoordinator] presentRestoredRound error message=${describe(e)}`);
      this.deps.presentationBarrier?.release(BARRIER_KEY);
      await this.joinPreSpinAfterError(preSpin);
      this.finishIdle();
      throw e;
    }
  }

  // ── A round ─────────────────────────────────────────────────────────────────────────────

  private async requestRound(input: { requestedFeatureIdentifier?: string }): Promise<void> {
    if (this.phase !== "idle") {
      this.log(`[SpinCoordinator] requestRound ignored phase=${this.phase}`);
      return;
    }
    if (this.deps.betSystem.isLocked()) {
      this.log(`[SpinCoordinator] requestRound ignored betLocked reason=${this.deps.betSystem.getLockReason()}`);
      return;
    }
    if (this.pendingGamble) {
      this.log(`[SpinCoordinator] gamble forfeited by new spin round=${this.pendingGamble.roundIdentifier}`);
      this.pendingGamble = null;
    }
    this.log(`[SpinCoordinator] requestRound begin mode=${this.speedMode} feature=${input.requestedFeatureIdentifier ?? "none"}`);
    this.deps.betSystem.lock("spin_request_in_flight");
    this.setPhase("requesting");
    this.roundPresented = false;
    const preSpin = this.holdBarrierAndPreSpin();
    // Read BEFORE `buildSpinRequest`, which deducts the round's cost optimistically. A rejection is
    // allowed to arrive without a balance snapshot (the field is optional on the contract), and a
    // known rejection is dismissed with OK and leaves the player in the game — so without this the
    // deduction for a bet that was never taken stays on the readout until the next spin response.
    const balanceBeforeRequest = this.deps.balanceStore.getSnapshot().balanceMinor;
    // This request's round, once its reply names one: a failure before that has no round of its own.
    let roundId: string | null = null;
    try {
      const request = this.deps.buildSpinRequest(input);
      await this.awaitBeforeRound();
      const result = await this.deps.requestPlay(request, this.deps.buildReplayConfiguration(this.speedMode));
      if (result.kind === "rejected") {
        await this.finishRejected(result.rejectedResponse, preSpin, balanceBeforeRequest);
        return;
      }
      roundId = result.spinResponse.roundSnapshot.roundIdentifier;
      await this.presentRound(result, preSpin);
      this.roundPresented = true;
      await this.settleRound(result);
      this.offerGamble(result.spinResponse.roundSnapshot);
      this.finishIdle();
      this.log(`[SpinCoordinator] requestRound complete round=${this.lastRoundId}`);
    } catch (e) {
      await this.failRound(e, preSpin, roundId);
    }
  }

  /**
   * The title's wait before a round (decision 71): a failure is logged and the round goes on, and so it does, logged,
   * past `BEFORE_ROUND_MAX_SECONDS` of engine time.
   */
  private async awaitBeforeRound(): Promise<void> {
    const { beforeRound, timer } = this.deps;
    if (!beforeRound) return;
    const settled = (async () => {
      try {
        await beforeRound();
      } catch (error) {
        this.log(`[SpinCoordinator] beforeRound error message=${describe(error)}`);
      }
      return "settled" as const;
    })();
    const bound = timer?.(BEFORE_ROUND_MAX_SECONDS);
    const outcome = bound ? await Promise.race([settled, bound.done.then(() => "bounded" as const)]) : await settled;
    bound?.cancel();
    if (outcome === "bounded") this.log(`[SpinCoordinator] beforeRound still waiting after ${BEFORE_ROUND_MAX_SECONDS}s: the round goes on`);
  }

  /** Holds the "spin" barrier and starts the pre-spin; the pre-spin releases the hold, or its failure does. */
  private holdBarrierAndPreSpin(): Promise<void> {
    const barrier = this.deps.presentationBarrier;
    this.log(`[SpinCoordinator] barrier hold key=${BARRIER_KEY}`);
    barrier?.hold(BARRIER_KEY);
    return (async () => {
      try {
        await this.deps.preSpin?.();
      } catch (error) {
        this.log(`[SpinCoordinator] preSpin error releasing barrier key=${BARRIER_KEY}`);
        barrier?.release(BARRIER_KEY);
        throw error;
      }
    })();
  }

  private async finishRejected(rejected: RejectedSpinResponse, preSpin: Promise<void>, balanceBeforeRequest: string): Promise<void> {
    const { statusCode, statusMessage } = rejected.responseMetadata;
    const serverBalance = rejected.accountBalanceSnapshot?.balanceAmount;
    // The server's snapshot is the authority whenever it sends one; otherwise the optimistic
    // deduction is put back, since the round never happened.
    const balanceAmount = serverBalance ?? balanceBeforeRequest;
    this.log(`[SpinCoordinator] requestPlay rejected code=${String(statusCode)} message="${statusMessage}" balance=${serverBalance ?? "n/a"} restoredFrom=${serverBalance ? "server" : "pre-request"}`);
    // The bonus-buy UI hides when the operator sends no feature prices; this status means the gate is out of sync.
    if (statusMessage === "feature_purchase_disabled") this.log("[SpinCoordinator] feature_purchase_disabled — the bonus-buy UI should not have been reachable");
    try {
      await preSpin;
    } catch (preSpinError) {
      this.log(`[SpinCoordinator] rejected spin preSpin cleanup error message=${describe(preSpinError)}`);
    } finally {
      this.deps.presentationBarrier?.release(BARRIER_KEY);
    }
    await this.deps.onSpinRejected?.({ statusCode, statusMessage, balanceAmount });
    this.finishIdle();
    this.log("[SpinCoordinator] requestRound complete rejected -> idle");
  }

  private async presentRound(result: RequestPlayOkResult, preSpin: Promise<void>): Promise<void> {
    this.lastRoundId = result.spinResponse.roundSnapshot.roundIdentifier;
    this.log(`[SpinCoordinator] requestPlay complete round=${this.lastRoundId}`);
    const spinBalance = result.spinResponse.accountBalanceSnapshot?.balanceAmount;
    if (spinBalance) this.deps.balanceStore.setBalanceMinor(spinBalance);
    this.setPhase("presenting");
    await Promise.all([result.presentationPromise, preSpin]);
    this.log(`[SpinCoordinator] presenting complete round=${this.lastRoundId}`);
  }

  private async settleRound(result: RequestPlayOkResult): Promise<void> {
    if (!result.settlementPromise) {
      this.log(`[SpinCoordinator] no settlementPromise round=${this.lastRoundId}`);
      return;
    }
    this.setPhase("settling");
    const settlement = await result.settlementPromise;
    // The provider refuses a settlement without a whole-minor-unit balance (review 2026-09-27 #3).
    const settledBalance = settlement.accountBalanceSnapshot.balanceAmount;
    this.deps.balanceStore.setBalanceMinor(settledBalance);
    this.log(`[SpinCoordinator] settlement complete round=${this.lastRoundId} balance=${settledBalance}`);
  }

  private offerGamble(roundSnapshot: RoundSnapshot): void {
    const offered = this.deps.onGamblePhase !== undefined && (this.deps.shouldOfferGamble?.(roundSnapshot) ?? false);
    this.pendingGamble = offered
      ? { roundIdentifier: roundSnapshot.roundIdentifier, possibleClientActions: roundSnapshot.possibleClientActions ?? [], gambleOfferDetails: roundSnapshot.gambleOfferDetails }
      : null;
    if (this.pendingGamble) {
      const details = roundSnapshot.gambleOfferDetails;
      this.log(`[SpinCoordinator] gamble available round=${this.lastRoundId} options=${details?.options.length ?? 0} attemptsRemaining=${details?.maxAttemptsRemaining ?? "n/a"}`);
    }
  }

  private async failRound(e: unknown, preSpin: Promise<void>, roundId: string | null): Promise<void> {
    const errorMessage = describe(e);
    const kind = inferSpinFailureKind(e);
    const statusMessage = e instanceof SpinTransportError ? e.statusMessage : undefined;
    const presented = this.roundPresented;
    // A lost reply names no round. Until 2026-09-26 this printed the previous round's id (review finding 83).
    this.log(`[SpinCoordinator] requestRound error round=${roundId ?? "none"} kind=${kind} message=${errorMessage}`);
    // Release is a no-op when the pre-spin already released it.
    this.deps.presentationBarrier?.release(BARRIER_KEY);
    await this.joinPreSpinAfterError(preSpin);
    // Before the idle transition: the autospin presenter fires the next spin the moment the phase is idle,
    // and the handler stops autospin first.
    if (this.deps.onSpinError) {
      try {
        await this.deps.onSpinError({ errorMessage, kind, statusMessage, presented });
      } catch (handlerError) {
        this.log(`[SpinCoordinator] onSpinError handler failed message=${describe(handlerError)}`);
      }
    }
    this.finishIdle();
    this.log("[SpinCoordinator] requestRound ended in error -> idle");
    if (!this.deps.onSpinError) throw e;
  }

  private async joinPreSpinAfterError(preSpin: Promise<void>): Promise<void> {
    await preSpin.catch((preSpinError) => {
      this.log(`[SpinCoordinator] preSpin join after error also failed message=${describe(preSpinError)}`);
    });
  }

  private finishIdle(): void {
    this.deps.betSystem.unlock();
    this.setPhase("idle");
  }

  private setPhase(next: SpinPhase): void {
    if (this.phase === next) return;
    this.log(`[SpinCoordinator] phase ${this.phase} -> ${next}`);
    this.phase = next;
    this.emit();
  }

  /**
   * Hands the current snapshot to every listener. A listener may start the next round synchronously
   * (autospin on idle); its transition then waits until this pass has finished and runs as a pass of
   * its own, so every listener sees every state in order and none receives one that is already false
   * (finding 5, 2026-09-14).
   */
  private emit(): void {
    if (this.emitting) {
      this.emitAgain = true;
      return;
    }
    this.emitting = true;
    try {
      do {
        this.emitAgain = false;
        const snapshot = this.getSnapshot();
        for (const listener of this.listeners) listener(snapshot);
      } while (this.emitAgain);
    } finally {
      this.emitting = false;
    }
  }
}

function describe(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

/**
 * A thrown error's failure kind: the provider's own classification, "protocol" for every engine
 * validation error (they share one base class), and "unexpected" for anything else — a bug, not a
 * transport event. Every kind ends in a reload; the kind picks the words.
 */
function inferSpinFailureKind(e: unknown): SpinFailureKind {
  if (e instanceof SpinTransportError) return e.kind;
  if (e instanceof ProtocolError) return "protocol";
  return "unexpected";
}
